import type { PostContentObjectList, UploadedFile } from 'models';
import { type RefObject, useCallback } from 'react';
import { usePermissions } from 'permissions';
import { Button, Col, Form, Icon, type RichTextEditorBase, RichTextInput, Row } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- break circular dependency
import { CommunitySelectWithUserProfilePhoto } from 'features/communities/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- break circular dependency
import type { CommunityLabelInValue } from 'features/communities/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- break circular dependency
import { useCommunityMentions } from 'features/communities/hooks';
import { AttachmentInput } from '../comment-form';
import { FileAttachments } from '../comment-form/file-attachments';
import { PinnedPostToggle } from '../pinned-post-toggle';
import { useAttachmentsRules, useCommunityRules, useGetInitialValues, useHtmlContentRules } from './hooks';

export type PostFormValues = {
  attachments: { files: UploadedFile[]; loading: boolean };
  community?: CommunityLabelInValue;
  htmlContent: string;
  pinned: boolean;
  textContent: string;
};

type P = {
  editorRef?: RefObject<RichTextEditorBase>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  onSubmit: (values: PostFormValues) => void;
  postContentObject?: PostContentObjectList;
  type?: 'announcement' | 'journal' | 'post';
};

const contentLabelByType = {
  announcement: 'Announcement',
  journal: 'Note',
  post: 'Post',
};

const PostForm = ({ editorRef, isLoading, mode, onSubmit, postContentObject, type = 'post' }: P) => {
  const [form] = Form.useForm<PostFormValues>();
  const formInitialValues = useGetInitialValues(postContentObject);
  const { canManageSelectedCommunity } = usePermissions();
  const attachments = Form.useWatch<PostFormValues['attachments'] | undefined>('attachments', form) ?? {
    files: [],
    loading: false,
  };
  const community = Form.useWatch<PostFormValues['community']>('community', form);
  const communityRules = useCommunityRules();
  const htmlContentRules = useHtmlContentRules();
  const attachmentsRules = useAttachmentsRules();
  const getCommunityId = useCallback(() => form.getFieldsValue().community?.value, [form]);
  const handleOnRequestMentions = useCommunityMentions(getCommunityId);

  return (
    <Form<PostFormValues> form={form} initialValues={formInitialValues} onFinish={onSubmit}>
      <Row>
        {type === 'post' ? (
          <Col span={24}>
            <Form.Item<PostFormValues> name="community" rules={communityRules}>
              <CommunitySelectWithUserProfilePhoto />
            </Form.Item>
          </Col>
        ) : null}

        <Col span={24}>
          <RichTextInput
            html={{
              className: 'post-form__editor',
              dependencies: ['attachments'],
              editorRef,
              isFocusedInitially: true,
              label: contentLabelByType[type],
              mentions: handleOnRequestMentions,
              name: 'htmlContent',
              noOptional: true,
              placeholder: 'Write content...',
              rules: htmlContentRules,
            }}
            text={{ name: 'textContent' }}
          />
        </Col>

        <Col className="post-form__attachments" span={24}>
          {attachments.files.length > 0 ? (
            <FileAttachments
              attachments={attachments.files}
              onClear={() => {
                form.setFieldsValue({ attachments: { files: [], loading: attachments.loading } });
              }}
              onRemove={(attachment) => {
                form.setFieldsValue({
                  attachments: {
                    files: attachments.files.filter((file) => file !== attachment),
                    loading: attachments.loading,
                  },
                });
              }}
            />
          ) : null}
        </Col>

        <Col span={24}>
          <Row align="middle" justify="space-between">
            <Col>
              <Form.Item<PostFormValues> name="attachments" noStyle rules={attachmentsRules}>
                <AttachmentInput />
              </Form.Item>
            </Col>
            <Col>
              <Row align="middle" gutter={['lg', 'lg']}>
                {type === 'post' && canManageSelectedCommunity({ community }) ? (
                  <Col>
                    <Form.Item<PostFormValues> name="pinned" noStyle>
                      <PinnedPostToggle />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col>
                  <Button
                    htmlType="submit"
                    icon={mode === 'create' ? <Icon icon="send-line" /> : undefined}
                    loading={isLoading}
                    type="primary"
                  >
                    {mode === 'create' ? 'Post' : 'Save'}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default PostForm;
